import React from "react";
import { Link } from "react-router-dom";
import "./styles/ContactCenter.css";

const channels = [
  {
    name: "Voice",
    text: "Handle traditional customer calls while keeping them connected to a broader service workflow.",
  },
  {
    name: "Chat",
    text: "Support real time conversations for customers who prefer faster digital interactions.",
  },
  {
    name: "SMS",
    text: "Extend service into mobile friendly communication for updates, follow ups, and quick support exchanges.",
  },
  {
    name: "Digital routing",
    text: "Unify multiple channels so conversations can be directed more efficiently across teams and queues.",
  },
];

const routingFlow = [
  {
    step: "01",
    title: "Customers reach out on their preferred channel",
    text: "CCaaS supports customer engagement beyond voice by including digital channels like chat and SMS, helping businesses align with changing customer preferences.",
  },
  {
    step: "02",
    title: "Requests enter a connected service environment",
    text: "Instead of managing interactions in separate systems, businesses can work through a more unified contact center model that centralizes communication flow.",
  },
  {
    step: "03",
    title: "Routing directs customers to the right agent",
    text: "Intelligent routing helps move customers toward the most appropriate agent more quickly, improving efficiency and reducing unnecessary handoffs.",
  },
  {
    step: "04",
    title: "Teams scale and adapt as demand changes",
    text: "Because CCaaS is cloud based, businesses can respond more flexibly to demand spikes, changing workloads, and distributed service operations.",
  },
];

const pillars = [
  {
    title: "Omnichannel customer support",
    text: "Meet customers across voice and digital touchpoints without forcing every interaction into a call only model.",
  },
  {
    title: "On demand scalability",
    text: "Support changing service volumes more effectively with a cloud based platform that can adapt as business needs shift.",
  },
  {
    title: "Global reach",
    text: "Extend customer service operations across broader regions while maintaining a more connected support structure.",
  },
  {
    title: "Easy integrations",
    text: "Connect CCaaS more easily with business systems so agents can work with better visibility and context.",
  },
];

const outcomeStats = [
  {
    label: "Faster routing",
    text: "Guide customers toward the right resource with less friction and better efficiency.",
  },
  {
    label: "Better channel coverage",
    text: "Support modern preferences with chat, SMS, and digital first engagement paths.",
  },
  {
    label: "More flexible operations",
    text: "Adapt to demand changes without relying on rigid on premise support models.",
  },
];

const fitCards = [
  {
    title: "Customer support teams",
    text: "For businesses that need quicker routing, stronger consistency, and support across multiple customer channels.",
  },
  {
    title: "Growing service operations",
    text: "For organizations that need more scalable service infrastructure as support demand increases.",
  },
  {
    title: "Distributed contact environments",
    text: "For teams operating across locations, regions, or mixed digital and voice support workflows.",
  },
];

export default function ContactCenter() {
  return (
    <div className="ccaasPage">
      <section className="ccaasHero">
        <div className="ccaasContainer ccaasHero__container">
          <div className="ccaasHero__content">
            <p className="ccaasEyebrow">Contact Center as a Service</p>

            <h1 className="ccaasHero__title">
              Customer support built for voice, digital channels, and faster routing.
            </h1>

            <p className="ccaasHero__text">
              Contact Center as a Service goes beyond traditional voice only
              support by helping businesses adopt digital channels such as chat
              and SMS alongside call handling. It creates a more flexible
              contact center environment with on demand scalability, global
              reach, easier integrations, and more efficient routing to the
              right agent.
            </p>

            <div className="ccaasHero__actions">
              <Link to="/contact" className="ccaasBtn ccaasBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="ccaasBtn ccaasBtn--ghost">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="ccaasHero__visual">
            <div className="ccaasOpsCard">
              <div className="ccaasOpsCard__top">
                <span className="ccaasBadge">Omnichannel support</span>
                <span className="ccaasBadge ccaasBadge--soft">Smart routing</span>
              </div>

              <div className="ccaasOpsCard__queue">
                <div className="ccaasOpsCard__incoming">
                  <span>Voice</span>
                  <span>Chat</span>
                  <span>SMS</span>
                </div>

                <div className="ccaasOpsCard__arrow">→</div>

                <div className="ccaasOpsCard__router">
                  <strong>Routing Layer</strong>
                  <p>Directs customers to the right agent faster</p>
                </div>
              </div>

              <div className="ccaasOpsCard__agents">
                <div className="ccaasAgentBox">
                  <strong>Agent A</strong>
                  <span>Priority queue</span>
                </div>
                <div className="ccaasAgentBox">
                  <strong>Agent B</strong>
                  <span>Digital support</span>
                </div>
                <div className="ccaasAgentBox">
                  <strong>Agent C</strong>
                  <span>Escalation path</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ccaasRailSection">
        <div className="ccaasContainer">
          <div className="ccaasRail">
            {channels.map((item) => (
              <article key={item.name} className="ccaasRailCard">
                <h3>{item.name}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccaasFlowSection">
        <div className="ccaasContainer">
          <div className="ccaasSectionHead ccaasSectionHead--center">
            <p className="ccaasEyebrow">How the model works</p>
            <h2 className="ccaasTitle">
              A service flow built around customer experience and agent efficiency
            </h2>
            <p className="ccaasText">
              CCaaS helps businesses modernize customer engagement by supporting
              more channels, more responsive routing, and a more scalable way to
              manage contact center operations.
            </p>
          </div>

          <div className="ccaasFlowGrid">
            {routingFlow.map((item) => (
              <article key={item.step} className="ccaasFlowCard">
                <div className="ccaasFlowCard__step">{item.step}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccaasPillarsSection">
        <div className="ccaasContainer ccaasPillarsWrap">
          <div className="ccaasPillarsIntro">
            <p className="ccaasEyebrow">Core strengths</p>
            <h2 className="ccaasTitle">
              The capabilities that make CCaaS effective for modern support teams
            </h2>
            <p className="ccaasText">
              Contact center environments need to support customer expectations,
              agent workflows, and changing service demand at the same time.
              CCaaS helps bring those needs together in a more adaptable model.
            </p>
          </div>

          <div className="ccaasPillarsList">
            {pillars.map((item) => (
              <article key={item.title} className="ccaasPillarItem">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccaasStatsSection">
        <div className="ccaasContainer">
          <div className="ccaasStatsWrap">
            <div className="ccaasStatsIntro">
              <p className="ccaasEyebrow">Operational value</p>
              <h2 className="ccaasTitle">
                Why businesses move toward a cloud contact center model
              </h2>
              <p className="ccaasText">
                The biggest value of CCaaS is not just adding more channels. It
                is creating a more responsive and manageable support environment
                that helps route interactions more efficiently and adapt to
                changing customer demand.
              </p>
            </div>

            <div className="ccaasStatsGrid">
              {outcomeStats.map((item) => (
                <article key={item.label} className="ccaasStatCard">
                  <strong>{item.label}</strong>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ccaasFitSection">
        <div className="ccaasContainer">
          <div className="ccaasSectionHead">
            <p className="ccaasEyebrow">Best fit</p>
            <h2 className="ccaasTitle">
              Where this service creates the strongest impact
            </h2>
            <p className="ccaasText">
              CCaaS works best in environments where customer expectations are
              rising, service channels are expanding, and routing efficiency has
              a direct impact on support quality.
            </p>
          </div>

          <div className="ccaasFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="ccaasFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccaasCta">
        <div className="ccaasContainer ccaasCta__container">
          <div>
            <p className="ccaasEyebrow ccaasEyebrow--light">Next step</p>
            <h2 className="ccaasCta__title">
              Build a more modern and responsive customer contact experience
            </h2>
            <p className="ccaasCta__text">
              If your business is looking to support customers across voice,
              chat, and SMS while improving scalability, routing, and service
              flexibility, ComNetworks.net can help you plan the right CCaaS
              approach.
            </p>
          </div>

          <div className="ccaasCta__actions">
            <Link to="/contact" className="ccaasBtn ccaasBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="ccaasBtn ccaasBtn--light">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
